import React, { useState, useEffect } from 'react';
import { CheckCircle, Server, Zap, Shield, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStorageUrl } from '../utils/imageUrlUpdater';

const VPSSingapore: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);

  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'VPS Singapore - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/vps-singapore';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const plans = [
    {
      name: 'Singapore – 1GB',
      price: '125,000',
      originalPrice: '150,000',
      cpu: '1 VCpu',
      ram: '1GB',
      storage: '30GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    },
    {
      name: 'Singapore – 2GB',
      price: '165,000',
      originalPrice: '200,000',
      cpu: '1 VCpu',
      ram: '2GB',
      storage: '30GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    },
    {
      name: 'Singapore – 2GB – 2 CPU',
      price: '209,000',
      originalPrice: '250,000',
      cpu: '2 VCpu',
      ram: '2GB',
      storage: '30GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: true
    },
    {
      name: 'Singapore – 4GB',
      price: '345,000',
      originalPrice: '415,000',
      cpu: '2 VCpu',
      ram: '4GB',
      storage: '60GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: true
    },
    {
      name: 'Singapore – 8GB',
      price: '665,000',
      originalPrice: '800,000',
      cpu: '4 VCpu',
      ram: '8GB',
      storage: '100GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    },
    {
      name: 'Singapore – 16GB',
      price: '1,255,000',
      originalPrice: '1,500,000',
      cpu: '8 VCpu',
      ram: '16GB',
      storage: '180GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    }
  ];

  const specifications = [
    { label: 'Datacenter', value: 'Singapore' },
    { label: 'Network', value: '1Gbps Port Speed' },
    { label: 'Storage', value: '100% SSD NVMe' },
    { label: 'Virtualization', value: 'KVM' },
    { label: 'Control Panel', value: 'VMon' },
    { label: 'Setup Time', value: 'Instant' },
    { label: 'IPv4', value: '1 Dedicated IP' },
    { label: 'Backup', value: 'Weekly Automatic' }
  ];

  const additionalPricing = [
    { item: '1 IP', price: '50,000 VNĐ / Tháng' },
    { item: '1 VCpu', price: '50,000 VNĐ / Tháng' },
    { item: '1 GB Ram', price: '50,000 VNĐ / Tháng' },
    { item: '10GB ổ cứng SSD NVMe', price: '30,000 VNĐ / Tháng' }
  ];

  const advantages = [
    {
      icon: Server,
      title: 'KHỞI TẠO NHANH CHÓNG, LINH HOẠT',
      description: 'Chỉ với vài click chuột trên trang quản lý của VMon, VPS Singapore của bạn đã được khởi tạo và thông tin chi tiết của VPS sẽ được gửi đến địa chỉ email mà bạn đã đăng ký.'
    },
    {
      icon: Shield,
      title: 'SỬ DỤNG 100% Ổ CỨNG NVME MẠNH MẼ',
      description: 'VPS Singapore sử dụng ổ cứng NVMe 100%, với tốc độ đọc/ghi vượt trội, mang đến trải nghiệm mượt mà không giới hạn cho bạn. Mọi thao tác đều diễn ra một cách trơn tru và nhanh nhẹn hơn bao giờ hết.'
    },
    {
      icon: Zap,
      title: 'SAO LƯU TỰ ĐỘNG HÀNG TUẦN',
      description: 'Tất cả VPS tại VMon Cloud đều được tự động sao lưu hàng tuần, mang đến lớp bảo vệ vững chắc cho dữ liệu của bạn, giúp đề phòng mọi rủi ro.'
    },
    {
      icon: Globe,
      title: 'ĐA DẠNG HỆ ĐIỀU HÀNH',
      description: 'VMon Cloud cung cấp đủ các hệ điều hành như Windows 10, Windows Server (2012r2, 2016, 2019, 2022), CentOS và Ubuntu Server, giúp bạn dễ dàng lựa chọn và triển khai theo ý muốn.'
    }
  ];

  const benefits = [
    {
      icon: Server,
      title: 'DỄ DÀNG QUẢN LÝ VPS SINGAPORE',
      description: 'Tại trang quản lý của VMon Cloud, bạn sẽ trải nghiệm đầy đủ tính năng giúp quản lý VPS Singapore một cách linh hoạt, dễ dàng, bất kỳ nơi nào và bất cứ khi nào bạn cần.'
    },
    {
      icon: Shield,
      title: 'DÙNG 7 NGÀY KHÔNG ƯNG - HOÀN TIỀN',
      description: 'Sự hài lòng của bạn là ưu tiên hàng đầu của chúng tôi. Nếu trong quá trình sử dụng dịch vụ có bất kỳ điều gì không đúng ý của bạn, chúng tôi cam kết hoàn trả 100% số tiền bạn đã thanh toán.'
    },
    {
      icon: Zap,
      title: 'THANH TOÁN ĐƠN GIẢN',
      description: 'Thanh toán và nạp tiền trở nên cực kỳ đơn giản thông qua việc quét mã QR. Hệ thống tự động xác nhận thanh toán chỉ trong vài giây sau khi quá trình hoàn tất.'
    },
    {
      icon: Globe,
      title: 'LUÔN SẴN SÀNG HỖ TRỢ 24/7',
      description: 'VMon Cloud hỗ trợ bạn qua nhiều kênh tiện ích như Facebook, Zalo, Telegram, hoặc hotline 0966.805.315. Hãy liên hệ ngay với chúng tôi nếu bạn cần trợ giúp.'
    }
  ];

  const faqItems = [
    {
      question: 'VPS Singapore máy chủ đặt tại đâu?',
      answer: 'VPS Singapore có máy chủ đặt tại thủ đô Singapore, nơi có hạ tầng hiện đại. Khi bạn mua VPS Singapore, VMon Cloud sẽ cung cấp cho bạn IP với Username, Password giúp bạn dễ dàng truy cập từ xa và sử dụng ngay lập tức.'
    },
    {
      question: 'Cài hệ điều hành cho VPS Singapore thế nào?',
      answer: 'VMon Cloud cung cấp đa dạng hệ điều hành cho VPS. Khi bạn khởi tạo dịch vụ, bạn có thể tự chọn và cài đặt hệ điều hành mình muốn sử dụng một cách tự động qua trang quản lý.'
    },
    {
      question: 'Tôi có được đổi IP khi thuê VPS Singapore không?',
      answer: 'Khi bạn sở hữu VPS Singapore và có nhu cầu thay đổi, VMon Cloud sẵn sàng hỗ trợ bạn chuyển đổi sang VPS ở quốc gia khác mà bạn mong muốn mà không tính thêm bất kỳ chi phí nào.'
    },
    {
      question: 'Tôi có cần trả phí cài đặt cho VPS Singapore không?',
      answer: 'Việc cài đặt VPS hoàn toàn được thực hiện trên trang quản lý với đầy đủ tính năng và hoàn toàn miễn phí.'
    },
    {
      question: 'Tôi có thể nâng cấp VPS Singapore khi cần không?',
      answer: 'Chỉ cần vài cú click chuột đơn giản, bạn có thể tạo ngay cấu hình VPS mới phù hợp với nhu cầu sử dụng của mình.'
    },
    {
      question: 'Mua VPS Singapore ở đâu?',
      answer: 'Có rất nhiều đơn vị uy tín cung cấp VPS Singapore. Bạn có thể lựa chọn tùy thuộc vào nhu cầu sử dụng. VMon Cloud là một trong những nhà cung cấp dịch vụ VPS chuyên nghiệp tại Việt Nam với đội ngũ kỹ thuật viên nhiều năm kinh nghiệm luôn sẵn sàng phục vụ.'
    }
  ];

  const testimonials = [
    getStorageUrl('feedback/feedback_1.jpg'),
    getStorageUrl('feedback/feedback_2.jpg'),
    getStorageUrl('feedback/feedback_3.jpg'),
    getStorageUrl('feedback/feedback_4.jpg'),
    getStorageUrl('feedback/feedback_5.jpg'),
    getStorageUrl('feedback/feedback12.jpg'),
    getStorageUrl('feedback/feedback16.jpg'),
    getStorageUrl('feedback/feedback17.jpg')
  ];

  const galleryImages = [
    {
      src: getStorageUrl('panel/Quan-ly-vps.PNG'),
      title: "Quản Lý VPS",
      description: "Quản lý các thông tin vps đầy đủ và tiện lợi."
    },
    {
      src: getStorageUrl('panel/View-VNC.PNG'),
      title: "View VPS qua VNC",
      description: "Truy cập xử lý sự cố vps thông qua VNC."
    },
    {
      src: getStorageUrl('panel/Mua-VPS.PNG'),
      title: "MUA VPS",
      description: "Mua VPS Singapore thuận tiện với giao diện one click."
    },
    {
      src: getStorageUrl('panel/giao-dien-mobile.PNG'),
      title: "Giao Diện Mobile",
      description: "Giao diện mobile thân thiện, dễ quản lý."
    },
    {
      src: getStorageUrl('panel/lich-su-so-du.PNG'),
      title: "Số Dư Tài Khoản",
      description: "Quản lý số dư tài khoản thuận tiện và minh bạch."
    },
    {
      src: getStorageUrl('panel/danh-sach-vps.PNG'),
      title: "Danh Sách VPS",
      description: "Xem danh sách VPS một cách thuận tiện, đầy đủ thông tin."
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Globe className="h-8 w-8 mr-2" />
              <span className="text-2xl font-semibold">VPS Singapore</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              VPS Singapore
            </h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Thuê VPS Singapore siêu tốc với 100% ổ cứng NVMe, sao lưu tự động hàng tuần, 
              đảm bảo an toàn dữ liệu và hiệu suất vượt trội.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Zap className="h-4 w-4 mr-2" />
                Băng thông 1GBPS
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Shield className="h-4 w-4 mr-2" />
                100% SSD NVMe
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Server className="h-4 w-4 mr-2" />
                99.9% Uptime
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">BẢNG GIÁ CLOUD VPS SINGAPORE</h2>
            </div>
          {/* Alert */}
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-8 rounded">
            <p className="font-bold">Ưu đãi hấp dẫn giảm ngay 40% khi gia hạn 3 năm.</p>
            <p>Giảm 20% khi gia hạn 1 năm, giảm 10% khi gia hạn 6 tháng, giảm 5% khi gia hạn 3 tháng.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.slice(0, 3).map((plan, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                  plan.popular ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-blue-500 text-white text-center py-2 font-semibold">
                    BÁN CHẠY
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-blue-600">{plan.price}</span>
                      <span className="text-gray-600 ml-1"> VNĐ/Tháng</span>
                    </div>
                    <div className="text-sm text-gray-500 line-through">{plan.originalPrice} VNĐ</div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-semibold">{plan.cpu}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">RAM:</span>
                      <span className="font-semibold">{plan.ram}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-semibold">{plan.storage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bandwidth:</span>
                      <span className="font-semibold">{plan.bandwidth}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Đăng Ký
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {plans.slice(3, 6).map((plan, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                  plan.popular ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-blue-500 text-white text-center py-2 font-semibold">
                    BÁN CHẠY
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-blue-600">{plan.price}</span>
                      <span className="text-gray-600 ml-1"> VNĐ/Tháng</span>
                    </div>
                    <div className="text-sm text-gray-500 line-through">{plan.originalPrice} VNĐ</div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-semibold">{plan.cpu}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">RAM:</span>
                      <span className="font-semibold">{plan.ram}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-semibold">{plan.storage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bandwidth:</span>
                      <span className="font-semibold">{plan.bandwidth}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Đăng Ký
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Pricing */}
          <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Bảng Giá Đăng Ký Thêm CPU, RAM, Ổ Cứng, IP
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalPricing.map((item, index) => (
                <div key={index} className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="font-semibold text-gray-900">{item.item}</span>
                  </div>
                  <p className="text-blue-600 font-medium">{item.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ƯU ĐIỂM KHI SỬ DỤNG VPS SINGAPORE TẠI VMON CLOUD
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <advantage.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{advantage.title}</h3>
                <p className="text-gray-600 text-sm">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              LỢI ÍCH KHI SỬ DỤNG VPS SINGAPORE TẠI VMON CLOUD
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Feedback Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">KHÁCH HÀNG NÓI GÌ VỀ CHÚNG TÔI?</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Tại VMon Cloud, ý kiến của khách hàng được đặt lên hàng đầu và chúng tôi luôn sẵn sàng lắng nghe và tiếp thu phản hồi để không ngừng cải thiện chất lượng sản phẩm và dịch vụ. Điều này giúp chúng tôi đáp ứng tốt hơn nhu cầu và mong muốn của khách hàng một cách tối đa.
            </p>
          </div>

          {/* Testimonial Slider */}
          <div className="relative max-w-4xl mx-auto">
            {/* Main Slider */}
            <div className="relative overflow-hidden rounded-lg shadow-lg bg-white">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 flex justify-center items-center p-4">
                    <img
                      src={testimonial}
                      alt={`Customer Feedback ${index + 1}`}
                      className="max-w-full max-h-96 object-contain rounded-lg shadow-sm"
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
              >
                <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
              >
                <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-blue-600 scale-110' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Thư Viện Hình Ảnh</h2>
          <p className="text-lg text-gray-600 mb-12 text-center">
            Quản lý tài khoản, tạo mới và giám sát khi Thuê VPS Việt Nam dễ dàng với trang panel hiện đại, thân thiện dễ sử dụng.
          </p>
          
          {/* Gallery Slider */}
          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-lg bg-gray-100">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentGallerySlide * 100}%)` }}
              >
                {galleryImages.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0 p-8">
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                      <img
                        src={image.src}
                        alt={image.title}
                        className="w-full h-64 object-contain bg-gray-50"
                      />
                      <div className="p-6 text-center">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{image.title}</h3>
                        <p className="text-gray-600">{image.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <button
              onClick={prevGallerySlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
            <button
              onClick={nextGallerySlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>
            
            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGallerySlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentGallerySlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thông Số Kỹ Thuật</h2>
            <p className="text-lg text-gray-600">Chi tiết cấu hình và tính năng</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specifications.map((spec, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">{spec.label}</div>
                <div className="font-semibold text-gray-900">{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Những câu hỏi thường gặp về máy chủ ảo Server Singapore
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-white rounded-lg p-6 shadow">
                <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600">
                  {item.question}
                </summary>
                <p className="mt-4 text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Cùng nhau, Trực tuyến.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.
          </p>
          <a 
            href="https://cloud.vmon.vn/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-block"
          >
            Bắt đầu ngay
          </a>
        </div>
      </section>
    </div>
  );
};

export default VPSSingapore;